import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import authService from '../services/api/auth.service';
import kioskService, { isKioskMode, kioskHome, PACKING_TERMINAL_PATH } from '../services/api/kiosk.service';
import { useAuth } from './auth-context';
import KioskLockOverlay from '../components/kiosk/KioskLockOverlay';
import KioskIdleChip from '../components/kiosk/KioskIdleChip';
import { KioskContext } from './kiosk-context';

/**
 * Public-terminal mode. See dezshop-operations/docs/kiosk-qr-auth.md.
 *
 * On an enrolled terminal there is no password login: the screen shows a QR,
 * the employee approves it in one tap from their phone, and the terminal runs
 * as that person until 30s of inactivity locks it again.
 *
 * The important design choice is LOCK, NOT LOGOUT. The overlay covers the
 * screen and the token is dropped, but the React tree is never unmounted — so
 * when the SAME person comes back they land on the same page with their
 * half-filled form intact. Without that, 30 seconds is a timeout that
 * sabotages itself: after losing work three times, someone puts a key on the
 * keyboard and the whole scheme is dead. A DIFFERENT person gets a full page
 * reload, which is the only way to change the identity AuthProvider hands to the
 * rest of the app — see finishUnlock.
 */

// Module scope, never storage: what to restore after a re-authorization. Route
// only — form state survives on its own because the tree stays mounted.
let restoreRoute = null;

/**
 * Not every path is worth coming back to. `/login` renders nothing at all in
 * kiosk mode (the password form is deliberately not mounted), so restoring it
 * would drop the terminal on a blank screen behind no overlay; `/` only
 * redirects, and the two kiosk routes are plumbing. Anything else is real work.
 */
function safeRestoreRoute(path) {
  if (!path) return null;
  const clean = path.split('?')[0];
  if (clean === '/' || clean === '/login' || clean.startsWith('/kiosk/') || clean.startsWith('/p/')) {
    return null;
  }
  return path;
}

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'wheel', 'touchstart', 'scroll'];
const ACTIVITY_THROTTLE_MS = 1000;
const TICK_MS = 250;
const HEARTBEAT_MS = 20000;

export function KioskProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const kioskEnabled = isKioskMode();

  const [terminal, setTerminal] = useState(null);
  // In kiosk mode we start covered and prove the session is live before
  // uncovering — never the other way round.
  const [locked, setLocked] = useState(kioskEnabled);
  const [resolving, setResolving] = useState(kioskEnabled);
  const [secondsLeft, setSecondsLeft] = useState(null);

  const lastActivityRef = useRef(Date.now());
  const lastBroadcastRef = useRef(0);
  const lastHeartbeatRef = useRef(0);
  const channelRef = useRef(null);
  const lockedRef = useRef(locked);
  const timeoutRef = useRef(30);

  lockedRef.current = locked;
  if (terminal?.idle_timeout_seconds) timeoutRef.current = terminal.idle_timeout_seconds;

  // --- Bootstrap: is this really a terminal, and is a session already live? ---
  useEffect(() => {
    if (!kioskEnabled) return;
    let cancelled = false;

    (async () => {
      const info = await kioskService.getTerminal();
      if (cancelled) return;
      if (!info) {
        // Revoked terminal (marker cleared server-side) or offline. Either way
        // there is nothing to unlock into; stay covered.
        setResolving(false);
        return;
      }
      setTerminal(info);
      // A second tab opened during a live session must not show the QR: minting
      // a token is the authoritative "is the session still open?" check.
      const token = await authService.refreshAccessToken();
      if (cancelled) return;
      lastActivityRef.current = Date.now();
      setLocked(!token);
      setResolving(false);
    })();

    return () => { cancelled = true; };
  }, [kioskEnabled]);

  useEffect(() => {
    if (terminal?.terminal_type === 'packing' && location.pathname !== PACKING_TERMINAL_PATH && location.pathname !== '/kiosk/start') {
      navigate(PACKING_TERMINAL_PATH, { replace: true });
    }
  }, [terminal?.terminal_type, location.pathname, navigate]);

  // --- Cross-tab channel ------------------------------------------------------
  useEffect(() => {
    if (!kioskEnabled || typeof BroadcastChannel === 'undefined') return;
    const channel = new BroadcastChannel('kiosk');
    channelRef.current = channel;

    channel.onmessage = (event) => {
      const message = event.data;
      if (!message?.type) return;
      if (message.type === 'activity') {
        // Activity anywhere counts everywhere: otherwise the person working in
        // tab 3 returns to tab 1 and finds it locked.
        if (message.at > lastActivityRef.current) lastActivityRef.current = message.at;
      } else if (message.type === 'locked') {
        applyLock();
      } else if (message.type === 'unlocked') {
        adoptSession(message.userId);
      }
    };

    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, [kioskEnabled]);

  function broadcast(message) {
    try { channelRef.current?.postMessage(message); } catch { /* channel closed */ }
  }

  /** Cover this tab. No server call — the initiating tab already made it. */
  function applyLock() {
    if (lockedRef.current) return;
    restoreRoute = safeRestoreRoute(`${window.location.pathname}${window.location.search}`);
    authService.clearAccessToken();
    setSecondsLeft(0);
    setLocked(true);
  }

  async function doLock(reason) {
    if (lockedRef.current) return;
    restoreRoute = safeRestoreRoute(`${window.location.pathname}${window.location.search}`);
    applyLock();
    broadcast({ type: 'locked' });
    await kioskService.lock(reason);
  }

  /** A tab learns (from the channel) that someone authorized the terminal. */
  async function adoptSession(userId) {
    const token = await authService.refreshAccessToken();
    if (!token) return;
    finishUnlock(userId);
  }

  function finishUnlock(userId) {
    // Identitatea din AuthProvider e sursa de adevar pentru interfata, si ea NU
    // se reimprospateaza singura cand se schimba tokenul. Daca persoana care a
    // autorizat e alta decat cea hidratata, singurul mod sigur de a schimba
    // identitatea in toata aplicatia e o reincarcare completa.
    //
    // Fara asta, terminalul rula cu tokenul persoanei noi dar afisa numele celei
    // precedente — si mai rau, ecranele care trimit `created_by_user_id` din
    // starea clientului (oferte, joburi de inventar, comentarii) ar fi scris
    // numele greșit in baza de date. Adica exact atribuirea greșita pe care
    // proiectul asta o repara. S-a intamplat in producție: un coleg a autorizat
    // si terminalul arata in continuare contul lui Adrian.
    const hydratedUserId = user?.userId ?? null;
    if (userId == null || userId !== hydratedUserId) {
      restoreRoute = null;
      // `location.replace` catre dashboard, nu `reload()`: reload pastreaza
      // URL-ul curent, iar in momentul deblocarii ruta e de obicei `/login`
      // (unde blocarea a prins aplicatia), care in modul terminal nu randeaza
      // nimic. Rezultatul era un ecran gol cu doar contorul pe el. Navigarea
      // catre alta adresa e tot o incarcare completa a paginii, deci resetarea
      // de identitate rămâne garantata.
      window.location.replace(kioskHome());
      return;
    }

    // Aceeasi persoana: revine exact unde era, cu formularul cum l-a lasat
    // (componentele au fost acoperite, niciodata demontate). Fallback-ul pe
    // dashboard conteaza — fara el, blocarea pe o ruta care nu randeaza nimic in
    // modul terminal lasa ecranul gol.
    lastActivityRef.current = Date.now();
    lastHeartbeatRef.current = 0;
    navigate(terminal?.terminal_type === 'packing' ? PACKING_TERMINAL_PATH : restoreRoute || kioskHome(), { replace: true });
    restoreRoute = null;
    setLocked(false);
  }

  /** Called by the overlay once its long-poll reports an approved pairing. */
  async function onPaired() {
    const token = await authService.refreshAccessToken();
    if (!token) return false;
    let userId = null;
    try {
      const response = await fetch(
        `${import.meta.env.VITE_API_BASE_URL || ''}/stoc/auth/v2/me`,
        { headers: { Authorization: `Bearer ${token}` }, credentials: 'include' }
      );
      if (response.ok) userId = (await response.json()).user_id;
    } catch { /* fall through: treated as a different user, i.e. a clean slate */ }
    broadcast({ type: 'unlocked', userId });
    finishUnlock(userId);
    return true;
  }

  // --- Activity tracking ------------------------------------------------------
  useEffect(() => {
    if (!kioskEnabled) return;

    const onActivity = () => {
      const now = Date.now();
      lastActivityRef.current = now;
      // While covered, input goes to the overlay only; it must not keep a dead
      // session's timer alive.
      if (lockedRef.current) return;
      if (now - lastBroadcastRef.current >= ACTIVITY_THROTTLE_MS) {
        lastBroadcastRef.current = now;
        broadcast({ type: 'activity', at: now });
      }
    };

    ACTIVITY_EVENTS.forEach((name) =>
      window.addEventListener(name, onActivity, { passive: true, capture: true })
    );
    return () => ACTIVITY_EVENTS.forEach((name) =>
      window.removeEventListener(name, onActivity, { capture: true })
    );
  }, [kioskEnabled]);

  // --- The countdown ----------------------------------------------------------
  useEffect(() => {
    if (!kioskEnabled || locked || resolving) return;

    const tick = setInterval(() => {
      const idleMs = Date.now() - lastActivityRef.current;
      const remaining = Math.ceil((timeoutRef.current * 1000 - idleMs) / 1000);
      setSecondsLeft(remaining > 0 ? remaining : 0);

      if (remaining <= 0) {
        doLock('idle');
        return;
      }

      // Only the visible tab beats, so six tabs don't mean six requests. Any
      // tab's heartbeat lands on the same kiosk_sessions row.
      const now = Date.now();
      if (
        document.visibilityState === 'visible' &&
        now - lastHeartbeatRef.current >= HEARTBEAT_MS &&
        idleMs < (terminal?.terminal_type === 'packing' ? timeoutRef.current * 1000 : HEARTBEAT_MS)
      ) {
        lastHeartbeatRef.current = now;
        kioskService.heartbeat().then((ok) => {
          // Fail-closed: a terminal that cannot reach the server locks rather
          // than sitting open on someone's identity.
          if (!ok) doLock('idle');
        });
      }
    }, TICK_MS);

    return () => clearInterval(tick);
  }, [kioskEnabled, locked, resolving, terminal?.terminal_type]);

  // --- Safety net: any 401 anywhere covers the screen -------------------------
  useEffect(() => {
    if (!kioskEnabled) return;
    const onSessionExpired = () => applyLock();
    window.addEventListener('auth:sessionExpired', onSessionExpired);
    return () => window.removeEventListener('auth:sessionExpired', onSessionExpired);
  }, [kioskEnabled]);

  if (!kioskEnabled) return children;

  return (
    <KioskContext.Provider
      value={{
        isKiosk: true,
        terminal,
        locked,
        secondsLeft,
        lock: () => doLock('manual'),
      }}
    >
      {children}
      {!locked && terminal?.terminal_type !== 'packing' && <KioskIdleChip secondsLeft={secondsLeft} onLogout={() => doLock('manual')} />}
      {locked && (
        <KioskLockOverlay
          terminal={terminal}
          resolving={resolving}
          onPaired={onPaired}
        />
      )}
    </KioskContext.Provider>
  );
}

KioskProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default KioskProvider;
